import {
  Icon,
  IconButton,
  Loading,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import difference from 'lodash/difference';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CONTACT_LIMIT_LIST, DEFAULT_CONTACT_LIMIT_LIST } from '@hero/constants';
import { useGetRecognition } from '@hero/services';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import { useRecogCalculateEstimatedCharge } from '@hero/services/order-service';
import { useNotification } from '@hero/store/hooks';
import { formatCurrency } from '@hero/utils';

import { ReviewAndSubmit } from '../Recognition/Detail/ReviewAndSubmit';

import {
  AUTOMATION_TABLE_TEMPLATE_SORTING,
  DRAFT_TABLE_TEMPLATE_SORTING,
  EDIT_RECIPIENT_STEP,
  LIVE_TABLE_TEMPLATE_SORTING
} from './constant';
import { EditModalContext } from './context';
import { NavigationSecondaryContent } from './NavigationSecondaryContent';
import * as S from './style';

function ReviewAndConfirm({
  selectedRows,
  contactRows,
  isLiveFeature,
  setModalObject,
  setStep,
  updateContacts,
  onRequestClose,
  onChangeSelectedRows,
  isCCPayment,
  featureType
}) {
  const { t } = useTranslation();
  const { setContextValue } = useContext(EditModalContext);

  const { showNotification } = useNotification();
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });

  // <--- TODO its for recognition we should remove it from here.
  const { recognitionCode } = useParams();
  const { mutateAsync: calculateRecognitionCharge, isLoading: isLoadingCalculation } =
    useRecogCalculateEstimatedCharge();
  const { data: recognition } = useGetRecognition({
    recognitionCode
  });
  // ---->

  const [newEstimatedCharge, setNewEstimatedCharge] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const contactRowsArray = useMemo(() => Object.keys(contactRows), [contactRows]);
  const selectedRowsArray = useMemo(() => Object.keys(selectedRows), [selectedRows]);

  const removedRecipients = useMemo(() => {
    if (isLiveFeature) {
      return [];
    }

    return difference(contactRowsArray, selectedRowsArray);
  }, [contactRowsArray, selectedRowsArray]);

  const additionalRecipients = useMemo(() => {
    if (isLiveFeature) {
      return selectedRowsArray;
    }

    return difference(selectedRowsArray, contactRowsArray);
  }, [contactRowsArray, selectedRowsArray]);

  const onSave = async () => {
    try {
      setIsLoading(true);
      const selectedContacts = Array.from(
        new Set([...Object.keys(selectedRows), ...(isLiveFeature ? Object.keys(contactRows) : [])])
      );

      const contactListLimit =
        merchantData?.configs[CONTACT_LIMIT_LIST] ?? DEFAULT_CONTACT_LIMIT_LIST;

      if (selectedContacts.length >= contactListLimit) {
        showNotification({
          message: t('recipientLimitMessage', {
            recipientLimit: contactListLimit
          }),
          title: t('recipientLimitTitle'),
          notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
          primaryButtonProps: {
            style: {
              margin: '0 auto'
            }
          }
        });

        return;
      }

      await updateContacts(selectedContacts);
      onRequestClose();

      if (isLiveFeature) {
        onChangeSelectedRows({});
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (recognitionCode && isLiveFeature) {
      (async () => {
        const calculated = await calculateRecognitionCharge({
          params: {
            code: recognitionCode
          },
          body: {
            products: recognition?.products,
            recipientCount: contactRowsArray.length + selectedRowsArray.length
          }
        });

        if (calculated.difference.total > 0) {
          setContextValue(calculated);
          setNewEstimatedCharge(calculated);
        } else {
          const estimated = {
            ...calculated,
            difference: {
              ...calculated.difference,
              total: 0
            }
          };
          setContextValue(estimated);
          setNewEstimatedCharge(estimated);
        }
      })();
    }
  }, []);

  useEffect(() => {
    setModalObject({
      title: (
        <S.ContactHeader>
          <IconButton
            color="neutral"
            onClick={() => {
              setStep(EDIT_RECIPIENT_STEP);
            }}
          >
            <Icon name="left-chevron" />
          </IconButton>
          <VerticalDivider $height="24px" $margin="0 16px 0 8px" />
          {t('reviewAndSubmit')}
        </S.ContactHeader>
      ),
      secondaryButtonProps:
        !isLiveFeature || featureType === 'automation'
          ? {
              children: t('back'),
              onClick: () => {
                setStep(EDIT_RECIPIENT_STEP);
              }
            }
          : null,
      primaryButtonProps: {
        onClick: onSave,
        children: t(isLiveFeature && isCCPayment ? 'payAndSubmit' : 'updateRecipientList'),
        disabled: isLoading || isLoadingCalculation,
        loading: isLoading
      },
      components:
        isLiveFeature && featureType !== 'automation'
          ? {
              SecondaryContent: NavigationSecondaryContent
            }
          : {}
    });
  }, [isLoading, isLoadingCalculation]);

  const table = useMemo(() => {
    const addedRecipient = {
      key: t('addedRecipients'),
      value: additionalRecipients.length,
      isActive: isLiveFeature && featureType !== 'automation'
    };
    const newRecipientTotal = {
      key: t('newRecipientTotal'),
      value: isLiveFeature
        ? contactRowsArray.length + selectedRowsArray.length
        : selectedRowsArray.length,
      isActive: !isLiveFeature || featureType === 'automation'
    };
    const removedRecipient = {
      key: t('removedRecipients'),
      value: removedRecipients.length,
      isHidden: !removedRecipients.length
    };
    const currentRecipient = {
      key: t('currentRecipients'),
      value: contactRowsArray.length
    };

    const availableKeys = { addedRecipient, currentRecipient, newRecipientTotal, removedRecipient };
    const sortingList = () => {
      if (featureType === 'automation') return AUTOMATION_TABLE_TEMPLATE_SORTING;
      else if (isLiveFeature) return LIVE_TABLE_TEMPLATE_SORTING;
      else return DRAFT_TABLE_TEMPLATE_SORTING;
    };

    const newTable = sortingList().map(t => {
      return availableKeys[t];
    });

    if (newEstimatedCharge && newEstimatedCharge?.difference?.total > 0) {
      const minEstimatedCharge = newEstimatedCharge?.new?.serviceCharges?.min;
      const maxEstimatedCharge = newEstimatedCharge?.new?.serviceCharges?.max;
      const shouldShowEstimatedChargeRange = minEstimatedCharge === 0 && maxEstimatedCharge !== 0;

      newTable.push({
        key: t('additionalCharge'),
        type: 'accordion',
        value: formatCurrency(newEstimatedCharge?.difference?.total),
        table: [
          {
            key: t('items'),
            helpText: `(${t('costSummarySubTitle1', {
              recipientCount: newEstimatedCharge?.difference?.recipientCount
            })})`,
            value: formatCurrency(newEstimatedCharge?.difference?.merchandise)
          },
          {
            key: t('estimatedTax'),
            value: formatCurrency(newEstimatedCharge?.difference?.estimatedTax)
          },
          {
            key: (
              <>
                {t('estimatedServiceCharge')}
                {shouldShowEstimatedChargeRange && (
                  <Typography variant="caption1" color="fg" emphasis="weak">
                    {t('serviceChargeMessageForFreeShip')}
                  </Typography>
                )}
              </>
            ),
            value: shouldShowEstimatedChargeRange ? (
              <>
                {formatCurrency(minEstimatedCharge)} - {formatCurrency(maxEstimatedCharge)}
              </>
            ) : (
              formatCurrency(newEstimatedCharge?.difference?.serviceCharge)
            )
          }
        ]
      });
    }

    return newTable.filter(n => !n.isHidden);
  }, [newEstimatedCharge]);

  if (isLoadingCalculation) {
    return (
      <S.LoadingWrapper>
        <Loading type="ring" color="var(--color-accent-primary-default)" />
      </S.LoadingWrapper>
    );
  }

  return (
    <ReviewAndSubmit
      icon="contacts"
      desc={t('reviewAndConfirmDescription')}
      table={table.filter(t => !!t)}
    />
  );
}

export { ReviewAndConfirm };
